import type { CSSProperties } from 'react';

export const SLIDE_ASPECT_WIDTH = 16;
export const SLIDE_ASPECT_HEIGHT = 9;
export const SLIDE_ASPECT_RATIO = SLIDE_ASPECT_WIDTH / SLIDE_ASPECT_HEIGHT;

export type SlideFrameRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type SlideBoxPercent = {
  x: number;
  y: number;
  w: number;
  h: number;
};

/**
 * 16:9 frame centered in the container, scaled to fit, never cropped.
 * At 1280×720 and 1920×1080 the frame fills the window with no bars.
 */
export function fitSlideFrame(
  containerWidth: number,
  containerHeight: number,
): SlideFrameRect {
  if (
    !Number.isFinite(containerWidth) ||
    !Number.isFinite(containerHeight) ||
    containerWidth <= 0 ||
    containerHeight <= 0
  ) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const containerAspect = containerWidth / containerHeight;
  if (containerAspect > SLIDE_ASPECT_RATIO) {
    const height = containerHeight;
    const width = height * SLIDE_ASPECT_RATIO;
    return {
      x: (containerWidth - width) / 2,
      y: 0,
      width,
      height,
    };
  }

  const width = containerWidth;
  const height = width / SLIDE_ASPECT_RATIO;
  return {
    x: 0,
    y: (containerHeight - height) / 2,
    width,
    height,
  };
}

/** CSS size that matches {@link fitSlideFrame} against the viewport. */
export const SLIDE_FRAME_WINDOW_SIZE = {
  width: `min(100vw, calc(100vh * ${SLIDE_ASPECT_WIDTH} / ${SLIDE_ASPECT_HEIGHT}))`,
  height: `min(100vh, calc(100vw * ${SLIDE_ASPECT_HEIGHT} / ${SLIDE_ASPECT_WIDTH}))`,
} as const;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Clamp a percent box to the slide edges (same rules as server validation):
 * x/y to 0–100, then shrink w/h so x+w ≤ 100 and y+h ≤ 100.
 * Returns null when no positive area remains.
 */
export function clampSlideBox(
  box: SlideBoxPercent | null | undefined,
): SlideBoxPercent | null {
  if (!box) {
    return null;
  }
  const x = clamp(box.x, 0, 100);
  const y = clamp(box.y, 0, 100);
  if (!(box.w > 0) || !(box.h > 0)) {
    return null;
  }
  const w = Math.min(box.w, 100 - x);
  const h = Math.min(box.h, 100 - y);
  if (w <= 0 || h <= 0) {
    return null;
  }
  return { x, y, w, h };
}

export function isFreeformLayout(layout: string | null | undefined): boolean {
  return layout === 'FREEFORM';
}

export type FreeformBackground = {
  type?: string | null;
  color?: string | null;
  imageUrl?: string | null;
};

export function resolveSlideBackgroundType(
  background: FreeformBackground | null | undefined,
): 'THEME' | 'COLOR' | 'IMAGE' {
  if (background?.type === 'COLOR' || background?.type === 'IMAGE') {
    return background.type;
  }
  return 'THEME';
}

export function shouldHideThemeStageElements(
  layout: string | null | undefined,
): boolean {
  return isFreeformLayout(layout);
}

/**
 * Editor safe-area inset as a percent of the 16:9 frame.
 * 40px at 1280×720 (60px at 1920×1080). Stage never draws this guide.
 */
export const SLIDE_SAFE = { x: 3.125, y: 5.556 };

export type SlidePercentRect = {
  x: number;
  y: number;
  w: number;
  h: number;
};

/**
 * Join CTA on FREEFORM Stage, as % of the 16:9 frame (not the window).
 * Union bound is x 80.1–98.9%, y 50.2–98.0% so it covers the drawn pill
 * (placed at y 51.3, bounce + shadow sit ~1.1% above). PR 3 reuses this for
 * the QR ghost. StageChrome parks the real CTA in pill + card.
 */
export const SLIDE_QR_ZONE = {
  x: 80.1,
  y: 50.2,
  w: 18.8,
  h: 47.8,
  pill: { x: 82.7, y: 51.3, w: 13.6, h: 4.7 },
  card: { x: 80.1, y: 57.1, w: 18.8, h: 40.9 },
} as const satisfies {
  x: number;
  y: number;
  w: number;
  h: number;
  pill: SlidePercentRect;
  card: SlidePercentRect;
};

/** Matches `activeEngagementTitle` in StageEngagementTitle.module.css. */
export const SLIDE_QR_PILL_PULSE_SCALE = 1.07;
export const SLIDE_QR_PILL_PULSE_LIFT_PX = 4;
/**
 * Title width as a fraction of the QR zone. 93% keeps a 1.07 pulse inside
 * x 80.1–98.9% (full card width would span ~79.44–99.56%).
 */
export const SLIDE_QR_PILL_WIDTH_OF_ZONE = 0.93;
/**
 * Unclipped FREEFORM title height as % of slide height. Measured ~39.6px
 * at 720p / ~59.4px at 1080p; 40px @ 720p covers rounding so the peak
 * stays at or below y 50.2%.
 */
export const SLIDE_QR_PILL_DRAWN_H = (40 / 720) * 100;

function slideQrPillWidthBox(): SlidePercentRect {
  const w = SLIDE_QR_ZONE.w * SLIDE_QR_PILL_WIDTH_OF_ZONE;
  return {
    x: SLIDE_QR_ZONE.x + (SLIDE_QR_ZONE.w - w) / 2,
    y: SLIDE_QR_ZONE.pill.y,
    w,
    h: SLIDE_QR_ZONE.pill.h,
  };
}

function slideQrPillPulseScaleHalf(): number {
  return (SLIDE_QR_PILL_DRAWN_H * (SLIDE_QR_PILL_PULSE_SCALE - 1)) / 2;
}

/** 720p lift % — the larger of 720p/1080p, so peak top can sit on the zone. */
function slideQrPillLiftPct(slideHeight: number): number {
  return (SLIDE_QR_PILL_PULSE_LIFT_PX / slideHeight) * 100;
}

/**
 * Rest bottom of the drawn pill (flex-end of the layout box). Shifted down
 * so translateY(-4px) scale(1.07) at 720p puts the drawn top on y 50.2%.
 */
export function slideQrPillRestBottom(): number {
  const restTop =
    SLIDE_QR_ZONE.y + slideQrPillLiftPct(720) + slideQrPillPulseScaleHalf();
  return restTop + SLIDE_QR_PILL_DRAWN_H;
}

/**
 * Drawn title rect at the pulse peak, as % of the 16:9 frame (glow excluded).
 */
export function slideQrPillDrawnPeak(slideHeight: number): {
  top: number;
  bottom: number;
} {
  const liftPct = slideQrPillLiftPct(slideHeight);
  const scaleHalf = slideQrPillPulseScaleHalf();
  const restBottom = slideQrPillRestBottom();
  const restTop = restBottom - SLIDE_QR_PILL_DRAWN_H;
  return {
    top: restTop - liftPct - scaleHalf,
    bottom: restBottom - liftPct + scaleHalf,
  };
}

/**
 * FREEFORM pill layout box: 93% zone width, centered. Box bottom is the
 * drawn rest bottom; top is the zone edge so the 720p peak sits on 50.2%.
 */
export function slideQrPillMaxBox(): SlidePercentRect {
  const widthBox = slideQrPillWidthBox();
  const restBottom = slideQrPillRestBottom();
  return {
    x: widthBox.x,
    y: SLIDE_QR_ZONE.y,
    w: widthBox.w,
    h: restBottom - SLIDE_QR_ZONE.y,
  };
}

/** Horizontal extent of the title pill at the 1.07 pulse peak, as % of the frame. */
export function slideQrPillPulsePeakX(): { left: number; right: number } {
  const box = slideQrPillWidthBox();
  const center = box.x + box.w / 2;
  const half = (box.w / 2) * SLIDE_QR_PILL_PULSE_SCALE;
  return { left: center - half, right: center + half };
}

export function percentRectOnFrame(
  frame: SlideFrameRect,
  box: SlidePercentRect,
): SlideFrameRect {
  return {
    x: frame.x + (frame.width * box.x) / 100,
    y: frame.y + (frame.height * box.y) / 100,
    width: (frame.width * box.w) / 100,
    height: (frame.height * box.h) / 100,
  };
}

/** QR card rect in window pixels. */
export function slideQrCardRect(frame: SlideFrameRect): SlideFrameRect {
  return percentRectOnFrame(frame, SLIDE_QR_ZONE.card);
}

/** QR card rect relative to the 16:9 frame origin. */
export function slideQrCardRectInFrame(
  containerWidth: number,
  containerHeight: number,
): SlideFrameRect {
  const frame = fitSlideFrame(containerWidth, containerHeight);
  const abs = slideQrCardRect(frame);
  return {
    x: abs.x - frame.x,
    y: abs.y - frame.y,
    width: abs.width,
    height: abs.height,
  };
}

/** Missing showQr means true. Only `false` hides the FREEFORM Stage CTA. */
export function resolveSlideShowQr(value: boolean | null | undefined): boolean {
  return value !== false;
}

/**
 * Known slide JSON keys for create/update. Same set the backend rebuilds from;
 * unknown keys are dropped. showQr must stay on this list.
 */
export const SLIDE_SUBMISSION_WRITE_KEYS = [
  'title',
  'content',
  'optionalImageUrl',
  'slideDuration',
  'imageTilt',
  'layout',
  'background',
  'elements',
  'showQr',
] as const;

export function pickSlideSubmissionWriteData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const picked: Record<string, unknown> = {};
  for (const key of SLIDE_SUBMISSION_WRITE_KEYS) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      picked[key] = data[key];
    }
  }
  return picked;
}

/**
 * FREEFORM Stage join CTA. MARKDOWN ignores showQr and always shows the sidebar.
 */
export function shouldShowFreeformStageJoinCta(options: {
  layout?: string | null;
  showQr?: boolean | null;
}): boolean {
  if (!isFreeformLayout(options.layout)) {
    return true;
  }
  return resolveSlideShowQr(options.showQr);
}

function percentRectsIntersect(
  a: SlidePercentRect,
  b: SlidePercentRect,
): boolean {
  return (
    a.x <= b.x + b.w && a.x + a.w >= b.x && a.y <= b.y + b.h && a.y + a.h >= b.y
  );
}

/** True when a text element's box intersects the Stage QR zone. */
export function textOverlapsQrZone(
  box: SlidePercentRect | null | undefined,
): boolean {
  const clamped = clampSlideBox(box);
  if (!clamped) {
    return false;
  }
  return percentRectsIntersect(clamped, {
    x: SLIDE_QR_ZONE.x,
    y: SLIDE_QR_ZONE.y,
    w: SLIDE_QR_ZONE.w,
    h: SLIDE_QR_ZONE.h,
  });
}

/** True when any part of the box sits outside the editor safe-area inset. */
export function isOutsideSafeArea(
  box: SlidePercentRect | null | undefined,
): boolean {
  const clamped = clampSlideBox(box);
  if (!clamped) {
    return false;
  }
  const left = SLIDE_SAFE.x;
  const top = SLIDE_SAFE.y;
  const right = 100 - SLIDE_SAFE.x;
  const bottom = 100 - SLIDE_SAFE.y;
  return (
    clamped.x < left ||
    clamped.y < top ||
    clamped.x + clamped.w > right ||
    clamped.y + clamped.h > bottom
  );
}

export type SlideThemeFonts = {
  headingFontFamily?: string[] | null;
  bodyFontFamily?: string[] | null;
};

/**
 * Explicit element fontFamily wins. Otherwise heading/body from the event theme.
 */
export function resolveSlideFontFamily(
  element: {
    fontFamily?: string[] | null;
    textType?: string | null;
  },
  theme?: SlideThemeFonts | null,
): string | undefined {
  if (element.fontFamily?.some((name) => Boolean(name))) {
    return element.fontFamily.filter(Boolean).join(',');
  }
  const role =
    element.textType === 'body' || element.textType === 'BODY'
      ? 'body'
      : 'heading';
  const family =
    role === 'body' ? theme?.bodyFontFamily : theme?.headingFontFamily;
  if (family?.some((name) => Boolean(name))) {
    return family.filter(Boolean).join(',');
  }
  return role === 'body'
    ? 'var(--screen-body-font-family)'
    : 'var(--screen-heading-font-family)';
}

/** Fan slide card: 16px margins, 8px padding → 342px inner at 390px wide. */
export const FAN_SLIDE_CARD = {
  marginPx: 16,
  paddingPx: 8,
  innerWidthAt390: 342,
} as const;

/**
 * FREEFORM join pill label and QR caption, as % of the 16:9 frame height.
 * The overlay is a size container so `cqh` tracks the frame, not the window.
 */
export const SLIDE_QR_LABEL_CQH = 2.6;
export const SLIDE_QR_LABEL_FONT_SIZE = `${SLIDE_QR_LABEL_CQH}cqh`;
export const SLIDE_QR_CAPTION_LINE_HEIGHT = 1.25;
export const SLIDE_QR_CAPTION_LINES = 2;
/** 2.6% × 1.25 × 2 = 6.5% of slide height (~47px at 720p, ~70px at 1080p). */
export const SLIDE_QR_CAPTION_RESERVE_CQH =
  SLIDE_QR_LABEL_CQH * SLIDE_QR_CAPTION_LINE_HEIGHT * SLIDE_QR_CAPTION_LINES;
export const SLIDE_QR_CAPTION_RESERVE_FONT_SIZE = `${SLIDE_QR_CAPTION_RESERVE_CQH}cqh`;

export function slideFrameJoinOverlayStyle(
  frame: SlideFrameRect,
): CSSProperties {
  return {
    position: 'fixed',
    left: frame.x,
    top: frame.y,
    width: frame.width,
    height: frame.height,
    zIndex: 30,
    pointerEvents: 'none',
    containerType: 'size',
  };
}

export function slidePercentBoxStyle(box: SlidePercentRect): CSSProperties {
  return {
    position: 'absolute',
    left: `${box.x}%`,
    top: `${box.y}%`,
    width: `${box.w}%`,
    height: `${box.h}%`,
    pointerEvents: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  };
}
